import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
// eslint-disable-next-line no-unused-vars
import { motion } from 'framer-motion';
import { LuFlaskConical, LuGamepad2, LuCalculator, LuSparkles } from 'react-icons/lu';
import { FiZap, FiEdit3 } from 'react-icons/fi';
import { useTheme } from '../../context/ThemeContext';
import { useIsViedma } from '../../lib/routing/ViedmaNavigation';

/**
 * Atmospheric Celestial Sky Background (Teen Edition)
 * Features a seamless animated Celestial Horizon Wipe when switching between Dark and Light modes.
 * In Viedma STEAM mode (/teen/viedma/*), renders the official Viedma STEAM aesthetic:
 * - 4-color top accent stripe
 * - Golden yellow sphere arc
 * - Crimson red curved arc with emerald rim
 * - Subtle floating STEAM icons
 */
export default function TeenBackground({ isViedma: propIsViedma }) {
  const { isDark } = useTheme();
  const location = useLocation();
  const isRouteViedma = useIsViedma();
  const isViedma =
    typeof propIsViedma === 'boolean'
      ? propIsViedma
      : isRouteViedma ||
        /^\/teen\/viedma(?:\/|$)/.test(location.pathname) ||
        location.pathname.includes('/viedma') ||
        (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('last_dashboard_portal') === 'viedma_teen') ||
        (typeof localStorage !== 'undefined' && localStorage.getItem('last_dashboard_portal') === 'viedma_teen');

  useEffect(() => {
    if (isViedma) {
      document.documentElement.classList.add('viedma-theme');
      document.body.classList.add('viedma-theme');
    } else {
      document.documentElement.classList.remove('viedma-theme');
      document.body.classList.remove('viedma-theme');
    }

    const topSkyColor = isViedma
      ? (isDark ? '#0c1723' : '#fffdf5')
      : (isDark ? '#050b1b' : '#4292e3');

    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    const originalColor = metaThemeColor?.getAttribute('content') || '#111113';

    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', topSkyColor);
    }

    document.documentElement.style.backgroundColor = topSkyColor;
    document.body.style.backgroundColor = topSkyColor;

    return () => {
      document.documentElement.classList.remove('viedma-theme');
      document.body.classList.remove('viedma-theme');
      document.documentElement.style.backgroundColor = '';
      document.body.style.backgroundColor = '';
      if (metaThemeColor) {
        metaThemeColor.setAttribute('content', originalColor);
      }
    };
  }, [isDark, isViedma]);

  if (isViedma) {
    return (
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden select-none" aria-hidden="true">
        {/* Base Canvas */}
        <div
          className="absolute inset-0 transition-colors duration-500"
          style={{
            backgroundColor: isDark ? '#0c1723' : '#fffdf5',
          }}
        />

        {/* Viedma Signature Golden-Yellow Sphere Arc (Top-Right Corner) */}
        <div
          className="absolute -top-28 sm:-top-36 md:-top-44 -end-24 sm:-end-28 md:-end-32 w-80 sm:w-96 md:w-[32rem] lg:w-[38rem] h-80 sm:h-96 md:h-[32rem] lg:h-[38rem] rounded-full pointer-events-none transition-all duration-700"
          style={{
            background: isDark
              ? 'radial-gradient(circle at 35% 35%, #ffc400 0%, #b88600 65%, rgba(184,134,0,0) 80%)'
              : 'radial-gradient(circle at 35% 35%, #ffd447 0%, #ffc400 60%, #ffa000 85%)',
            boxShadow: isDark
              ? '0 0 90px rgba(255, 196, 0, 0.28)'
              : '0 14px 70px rgba(255, 196, 0, 0.38)',
          }}
        />

        {/* Viedma Signature Crimson Red Curved Arc with Green Rim Stripe (Bottom Corner Accent) */}
        <div
          className="absolute -bottom-36 sm:-bottom-48 md:-bottom-56 -start-28 sm:-start-36 md:-start-44 w-72 sm:w-96 md:w-[28rem] lg:w-[32rem] h-48 sm:h-64 md:h-72 rounded-full pointer-events-none rotate-[-12deg] transition-all duration-700"
          style={{
            background: isDark
              ? 'radial-gradient(circle at 50% 50%, #c51e31 0%, #861a24 70%)'
              : 'radial-gradient(circle at 50% 50%, #ef1e2c 0%, #b91d29 70%)',
            boxShadow: '0 -14px 0 #11974a',
          }}
        />

        {/* Floating Subtle Teen STEAM Discovery Elements */}
        {/* 1. Science Beaker */}
        <motion.div
          animate={{ y: [0, -12, 0], rotate: [0, 4, 0] }}
          transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute top-[16%] start-[6%] sm:start-[8%] md:start-[10%] pointer-events-none opacity-60 dark:opacity-45"
        >
          <div className="p-3 sm:p-3.5 rounded-2xl bg-white/85 dark:bg-[#152635]/85 border-2 border-[#11974a]/30 shadow-md backdrop-blur-sm">
            <LuFlaskConical className="w-6 h-6 sm:w-7 sm:h-7 text-[#11974a]" />
          </div>
        </motion.div>

        {/* 2. Technology Energy Zap */}
        <motion.div
          animate={{ y: [0, 14, 0], rotate: [0, -5, 0] }}
          transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
          className="absolute top-[28%] end-[8%] sm:end-[12%] pointer-events-none opacity-60 dark:opacity-45"
        >
          <div className="p-3 sm:p-3.5 rounded-2xl bg-white/85 dark:bg-[#152635]/85 border-2 border-[#176c8e]/30 shadow-md backdrop-blur-sm">
            <FiZap className="w-6 h-6 sm:w-7 sm:h-7 text-[#176c8e] dark:text-[#78c8e8]" />
          </div>
        </motion.div>

        {/* 3. Engineering Gamepad */}
        <motion.div
          animate={{ y: [0, -10, 0], rotate: [0, -6, 0] }}
          transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
          className="absolute bottom-[20%] start-[10%] sm:start-[14%] pointer-events-none opacity-60 dark:opacity-45"
        >
          <div className="p-3 sm:p-3.5 rounded-2xl bg-white/85 dark:bg-[#152635]/85 border-2 border-[#ffc400]/40 shadow-md backdrop-blur-sm">
            <LuGamepad2 className="w-6 h-6 sm:w-7 sm:h-7 text-[#b88600] dark:text-[#ffc400]" />
          </div>
        </motion.div>

        {/* 4. Art Pencil */}
        <motion.div
          animate={{ y: [0, 12, 0], rotate: [0, 6, 0] }}
          transition={{ duration: 7.5, repeat: Infinity, ease: 'easeInOut', delay: 1.5 }}
          className="absolute bottom-[16%] end-[7%] sm:end-[10%] pointer-events-none opacity-60 dark:opacity-45"
        >
          <div className="p-3 sm:p-3.5 rounded-2xl bg-white/85 dark:bg-[#152635]/85 border-2 border-[#b91d29]/30 shadow-md backdrop-blur-sm">
            <FiEdit3 className="w-6 h-6 sm:w-7 sm:h-7 text-[#b91d29] dark:text-[#ff8d83]" />
          </div>
        </motion.div>

        {/* 5. Mathematics Calculator */}
        <motion.div
          animate={{ y: [0, -8, 0], scale: [1, 1.04, 1] }}
          transition={{ duration: 6.5, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
          className="absolute top-[8%] end-[32%] sm:end-[36%] pointer-events-none opacity-50 dark:opacity-35 hidden md:block"
        >
          <div className="p-2.5 rounded-2xl bg-white/85 dark:bg-[#152635]/85 border-2 border-[#75519d]/30 shadow-md backdrop-blur-sm">
            <LuCalculator className="w-5 h-5 text-[#75519d] dark:text-[#d1b2ef]" />
          </div>
        </motion.div>

        {/* 6. STEAM Sparkles */}
        <div className="absolute top-[22%] start-[30%] pointer-events-none opacity-40 dark:opacity-25">
          <LuSparkles className="w-5 h-5 text-[#ffc400] animate-pulse" />
        </div>
        <div className="absolute bottom-[30%] end-[25%] pointer-events-none opacity-35 dark:opacity-20">
          <LuSparkles className="w-4 h-4 text-[#11974a] animate-pulse" />
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden select-none" aria-hidden="true">
      {/* ─── Layer 1: Daytime Cerulean Sky (Base Layer) ─── */}
      <div
        className="absolute inset-0 z-0 overflow-hidden"
        style={{
          background: 'linear-gradient(180deg, #4292e3 0%, #5ba4eb 30%, #7dbbf3 65%, #9fd0f8 100%)',
        }}
      >
        {/* Soft Ambient Floating Daytime Clouds */}
        {/* Top-Left Cloud */}
        <svg
          className="absolute -top-10 -left-12 w-80 sm:w-[32rem] h-48 sm:h-64 teen-cloud-drift-1 text-white/35 drop-shadow-sm transition-transform duration-700"
          fill="currentColor"
          viewBox="0 0 24 24"
        >
          <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96z" />
        </svg>

        {/* Top-Center / Right Cloud */}
        <svg
          className="absolute -top-16 left-[46%] w-80 sm:w-[34rem] h-52 teen-cloud-drift-2 text-white/30 drop-shadow-xs transition-transform duration-700"
          fill="currentColor"
          viewBox="0 0 24 24"
        >
          <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96z" />
        </svg>

        {/* Bottom-Left Cloud */}
        <svg
          className="absolute -bottom-20 -left-16 w-96 sm:w-[40rem] h-64 text-white/35 drop-shadow-md transition-transform duration-700"
          fill="currentColor"
          viewBox="0 0 24 24"
        >
          <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96z" />
        </svg>

        {/* Bottom-Right Cloud */}
        <svg
          className="absolute -bottom-16 -right-16 w-80 sm:w-[34rem] h-56 text-white/30 drop-shadow-md transition-transform duration-700"
          fill="currentColor"
          viewBox="0 0 24 24"
        >
          <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96z" />
        </svg>

        {/* Daytime Solar Glints & 4-Point Diamond Accents */}
        <div className="absolute top-[8%] left-[23%] w-7 h-7 sm:w-9 sm:h-9 teen-float-1 teen-twinkle-1 text-white/80 drop-shadow-sm">
          <svg viewBox="0 0 24 24" className="w-full h-full" fill="currentColor">
            <path d="M12 1 C12 7 7 12 1 12 C7 12 12 17 12 23 C12 17 17 12 23 12 C17 12 12 7 12 1 Z" />
          </svg>
        </div>
        <div className="absolute top-[28%] left-[3.5%] w-6 h-6 sm:w-7 sm:h-7 teen-float-2 teen-twinkle-2 text-amber-200/90 drop-shadow-sm">
          <svg viewBox="0 0 24 24" className="w-full h-full" fill="currentColor">
            <path d="M12 1 C12 7 7 12 1 12 C7 12 12 17 12 23 C12 17 17 12 23 12 C17 12 12 7 12 1 Z" />
          </svg>
        </div>
        <div className="absolute top-[18%] right-[29%] w-6 h-6 sm:w-8 sm:h-8 teen-float-3 teen-twinkle-3 text-white/80 drop-shadow-sm">
          <svg viewBox="0 0 24 24" className="w-full h-full" fill="currentColor">
            <path d="M12 1 C12 7 7 12 1 12 C7 12 12 17 12 23 C12 17 17 12 23 12 C17 12 12 7 12 1 Z" />
          </svg>
        </div>
        <div className="absolute top-[14%] right-[24%] w-4 h-4 sm:w-5 sm:h-5 teen-twinkle-1 text-sky-100/90 drop-shadow-sm">
          <svg viewBox="0 0 24 24" className="w-full h-full" fill="currentColor">
            <path d="M12 1 C12 7 7 12 1 12 C7 12 12 17 12 23 C12 17 17 12 23 12 C17 12 12 7 12 1 Z" />
          </svg>
        </div>
        <div className="absolute bottom-[32%] right-[5%] w-7 h-7 sm:w-9 sm:h-9 teen-float-1 teen-twinkle-2 text-white/80 drop-shadow-sm">
          <svg viewBox="0 0 24 24" className="w-full h-full" fill="currentColor">
            <path d="M12 1 C12 7 7 12 1 12 C7 12 12 17 12 23 C12 17 17 12 23 12 C17 12 12 7 12 1 Z" />
          </svg>
        </div>

        {/* Micro-sparkle nodes */}
        <span className="absolute top-[22%] left-[12%] w-1.5 h-1.5 rounded-full bg-white/60 teen-twinkle-2" />
        <span className="absolute top-[48%] right-[12%] w-2 h-2 rounded-full bg-amber-100/60 teen-twinkle-3" />
        <span className="absolute bottom-[22%] left-[16%] w-1.5 h-1.5 rounded-full bg-white/60 teen-twinkle-1" />
      </div>

      {/* ─── Layer 2: Cosmic Night Sky (Smooth Atmospheric Crossfade) ─── */}
      <motion.div
        initial={false}
        animate={{
          opacity: isDark ? 1 : 0,
        }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="absolute inset-0 z-1 overflow-hidden pointer-events-none"
        style={{
          background: 'linear-gradient(180deg, #050b1b 0%, #0d1a3a 45%, #071026 100%)',
        }}
      >
        {/* Deep Cosmic Nebula Clouds */}
        <svg
          className="absolute -top-10 -left-12 w-80 sm:w-[32rem] h-48 sm:h-64 teen-cloud-drift-1 text-sky-950/40 drop-shadow-sm"
          fill="currentColor"
          viewBox="0 0 24 24"
        >
          <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96z" />
        </svg>
        <svg
          className="absolute -top-16 left-[46%] w-80 sm:w-[34rem] h-52 teen-cloud-drift-2 text-sky-950/30 drop-shadow-xs"
          fill="currentColor"
          viewBox="0 0 24 24"
        >
          <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96z" />
        </svg>
        <svg
          className="absolute -bottom-20 -left-16 w-96 sm:w-[40rem] h-64 text-sky-950/45 drop-shadow-md"
          fill="currentColor"
          viewBox="0 0 24 24"
        >
          <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96z" />
        </svg>
        <svg
          className="absolute -bottom-16 -right-16 w-80 sm:w-[34rem] h-56 text-sky-950/35 drop-shadow-md"
          fill="currentColor"
          viewBox="0 0 24 24"
        >
          <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96z" />
        </svg>

        {/* Ethereal Nebula Glow Wells */}
        <div className="absolute top-[15%] left-[20%] w-[32rem] h-[32rem] rounded-full bg-cyan-500/10 blur-[100px] pointer-events-none" />
        <div className="absolute bottom-[20%] right-[15%] w-[36rem] h-[36rem] rounded-full bg-indigo-500/10 blur-[120px] pointer-events-none" />

        {/* Luminous 4-Point Diamond Stars (✦) with Radiant Starlight Dropshadows */}
        <div className="absolute top-[8%] left-[23%] w-7 h-7 sm:w-9 sm:h-9 teen-float-1 teen-twinkle-1 text-cyan-300 drop-shadow-[0_0_12px_rgba(103,232,249,0.8)]">
          <svg viewBox="0 0 24 24" className="w-full h-full" fill="currentColor">
            <path d="M12 1 C12 7 7 12 1 12 C7 12 12 17 12 23 C12 17 17 12 23 12 C17 12 12 7 12 1 Z" />
          </svg>
        </div>
        <div className="absolute top-[28%] left-[3.5%] w-6 h-6 sm:w-7 sm:h-7 teen-float-2 teen-twinkle-2 text-sky-300 drop-shadow-[0_0_10px_rgba(125,211,252,0.8)]">
          <svg viewBox="0 0 24 24" className="w-full h-full" fill="currentColor">
            <path d="M12 1 C12 7 7 12 1 12 C7 12 12 17 12 23 C12 17 17 12 23 12 C17 12 12 7 12 1 Z" />
          </svg>
        </div>
        <div className="absolute top-[18%] right-[29%] w-6 h-6 sm:w-8 sm:h-8 teen-float-3 teen-twinkle-3 text-purple-300 drop-shadow-[0_0_12px_rgba(216,180,254,0.8)]">
          <svg viewBox="0 0 24 24" className="w-full h-full" fill="currentColor">
            <path d="M12 1 C12 7 7 12 1 12 C7 12 12 17 12 23 C12 17 17 12 23 12 C17 12 12 7 12 1 Z" />
          </svg>
        </div>
        <div className="absolute top-[14%] right-[24%] w-4 h-4 sm:w-5 sm:h-5 teen-twinkle-1 text-cyan-200 drop-shadow-[0_0_8px_rgba(165,243,252,0.8)]">
          <svg viewBox="0 0 24 24" className="w-full h-full" fill="currentColor">
            <path d="M12 1 C12 7 7 12 1 12 C7 12 12 17 12 23 C12 17 17 12 23 12 C17 12 12 7 12 1 Z" />
          </svg>
        </div>
        <div className="absolute bottom-[32%] right-[5%] w-7 h-7 sm:w-9 sm:h-9 teen-float-1 teen-twinkle-2 text-sky-300 drop-shadow-[0_0_10px_rgba(125,211,252,0.8)]">
          <svg viewBox="0 0 24 24" className="w-full h-full" fill="currentColor">
            <path d="M12 1 C12 7 7 12 1 12 C7 12 12 17 12 23 C12 17 17 12 23 12 C17 12 12 7 12 1 Z" />
          </svg>
        </div>

        {/* Luminous Constellation Stardust */}
        <span className="absolute top-[22%] left-[12%] w-1.5 h-1.5 rounded-full bg-cyan-300 opacity-80 shadow-[0_0_8px_#38bdf8] teen-twinkle-2" />
        <span className="absolute top-[48%] right-[12%] w-2 h-2 rounded-full bg-purple-300 opacity-75 shadow-[0_0_8px_#c084fc] teen-twinkle-3" />
        <span className="absolute bottom-[22%] left-[16%] w-1.5 h-1.5 rounded-full bg-sky-300 opacity-80 shadow-[0_0_8px_#38bdf8] teen-twinkle-1" />
      </motion.div>
    </div>
  );
}
